import { type FC, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { LoadingSpinner } from "widgets";

import { renderGenomeAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { type IGenomeState, shouldLoadGenome } from "@/redux/modules/genome.ts";
import { getClassicGenomeBrowserTracks } from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { TRACK_TYPE } from "@/settings/settings.js";

import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import { GenomeView } from "../genome-viewer/GenomeView.js";

const GenomeGenomeView: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [isLoading, setLoading] = useState(true);
  const { genome } = useSelector<IAppState, IGenomeState>((state) => state.genome, shallowEqual);
  const { genomeId } = useParams<"genomeId">();

  useEffect(() => {
    if (genomeId) {
      setLoading(true);
      dispatch(shouldLoadGenome(Number.parseInt(genomeId, 10)));
    }
  }, [dispatch, genomeId]);

  useEffect(() => {
    setLoading(false);
  }, [genome]);

  if (isLoading) {
    return (
      <div className={styleLayout.page}>
        <LoadingSpinner inCenter />
      </div>
    );
  }

  if (genome && genome.hasContigs) {
    return (
      <GenomeView
        genomeId={genome.id}
        genomes={[genome]}
        // Additional tracks from the URL are loaded from the GenomeView
        defaultTracks={getClassicGenomeBrowserTracks([TRACK_TYPE.SEQUENCE, TRACK_TYPE.GENE])}
        attributesWidgets={[
          <AttributesListWidget
            attributes={renderGenomeAttributesWidgets(genome)}
            key={"genome-genome-view-widget-1"}
          />,
        ]}
        title={genome.name}
        subtitle={genome.description || "Genome View"}
      />
    );
  } else {
    return (
      <div className={styleLayout.page}>
        <ContentPlaceholder
          text={"Genome view is not available because this genome is not sequenced or does not contain any contigs."}
        />
      </div>
    );
  }
};

export { GenomeGenomeView };
